import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { differenceInDays, isPast } from 'date-fns';

export const useTrialStatus = () => {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trialDaysLeft, setTrialDaysLeft] = useState(null);
  const [isTrialExpired, setIsTrialExpired] = useState(false);
  const [isSuspended, setIsSuspended] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [showTrialBanner, setShowTrialBanner] = useState(false);
  const [showPaymentPrompt, setShowPaymentPrompt] = useState(false);

  const checkTrialStatus = useCallback(async () => {
    try {
      setLoading(true);
      const { data: sub } = await supabase
        ?.from('org_subscriptions')
        ?.select('*')
        ?.limit(1)
        ?.single();

      if (!sub) {
        setLoading(false);
        return;
      }

      setSubscription(sub);

      const now = new Date();
      const trialEnd = sub?.trial_ends_at ? new Date(sub.trial_ends_at) : null;
      const suspended = !!sub?.account_suspended_at;
      const status = sub?.status;

      setIsSuspended(suspended);

      if (status === 'trialing' && trialEnd) {
        const expired = isPast(trialEnd);
        setIsTrialExpired(expired);

        if (expired) {
          setIsActive(false);
          setShowPaymentPrompt(true);
          setShowTrialBanner(false);
          // Auto-suspend if trial expired and no payment method
          if (!sub?.payment_method_added && !suspended) {
            await supabase?.from('org_subscriptions')?.update({
              account_suspended_at: now?.toISOString(),
              suspension_reason: 'Trial period expired without payment method',
              updated_at: now?.toISOString()
            })?.eq('id', sub?.id);
          }
        } else {
          const daysLeft = differenceInDays(trialEnd, now);
          setTrialDaysLeft(daysLeft);
          setIsActive(true);
          setShowPaymentPrompt(false);
          // Show banner when 5 or fewer days left
          setShowTrialBanner(daysLeft <= 5);
        }
      } else if (status === 'active') {
        setIsActive(true);
        setIsTrialExpired(false);
        setShowPaymentPrompt(false);
        setShowTrialBanner(false);
      } else if (status === 'past_due' || status === 'cancelled' || suspended) {
        setIsActive(false);
        setShowPaymentPrompt(true);
      } else {
        setIsActive(true);
      }
    } catch (err) {
      console.error('Trial status check error:', err?.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkTrialStatus();
  }, [checkTrialStatus]);

  const activateSubscription = async (planId, userCount, billingInfo) => {
    try {
      const { error } = await supabase?.from('org_subscriptions')?.update({
        status: 'active',
        plan: planId,
        max_users: userCount,
        payment_method_added: true,
        account_suspended_at: null,
        suspension_reason: null,
        ...billingInfo,
        updated_at: new Date()?.toISOString()
      })?.eq('id', subscription?.id);

      if (!error) {
        await checkTrialStatus();
        return { success: true };
      }
      return { success: false, error };
    } catch (err) {
      return { success: false, error: err };
    }
  };

  return {
    subscription,
    loading,
    trialDaysLeft,
    isTrialExpired,
    isSuspended,
    isActive,
    showTrialBanner,
    showPaymentPrompt,
    checkTrialStatus,
    activateSubscription
  };
};

export default useTrialStatus;
